import {
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useId,
} from 'react';

import Text from '@/components/ui/text';
import { Label } from '@/components/ui/label';
import {
  type FormSpacing,
  type InputWidth,
  type ValidationState,
  formSpacing,
  inputWidthSizes,
} from '@/theme/size-tokens';
import { type Alignment, inputAlignClasses } from '@/theme/tokens';

export type FormFieldProps = {
  /** Form input component - typically Input, Select, Textarea, etc. */
  children: ReactNode;
  /** Label text displayed above the input */
  label?: string;
  /** Error message to display */
  error?: string;
  /** Helper text displayed below input */
  helperText?: string;
  /** Optional action rendered beside helper/error text */
  helperAction?: ReactNode;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
  /** Shows required asterisk next to label */
  required?: boolean;
  /** Spacing between label, input, and helper text */
  spacing?: FormSpacing;
  /** Disables the form field and child input */
  disabled?: boolean;
  /** HTML id for the input - auto-generated if not provided */
  id?: string;
  /** Same as id - use when you want the label's htmlFor to match the input id (accessibility) */
  htmlFor?: string;
  /** Width of the form field - uses semantic sizing */
  width?: InputWidth;
  /** Aligns the form field - left, center, or right */
  align?: Alignment;
  /** Reserve space for helper/error text to prevent layout shift */
  reserveMessageSpace?: boolean;
  /** Additional CSS classes applied to the label element */
  labelClassName?: string;
};

/**
 * FormField - Simple wrapper for form inputs with label and error display
 *
 * For most use cases, prefer the unified components:
 * - FormInput - for text inputs
 * - FormSelect - for dropdowns
 * - FormTextarea - for multi-line text
 *
 * Use FormField when you need to wrap custom or third-party inputs.
 *
 * @example
 * ```tsx
 * <FormField label="Email" required error={errors.email}>
 *   <Input type="email" placeholder="you@example.com" />
 * </FormField>
 * ```
 */
function FormField({
  children,
  label,
  error,
  helperText,
  helperAction,
  required = false,
  spacing = 'sm',
  disabled = false,
  id: providedId,
  htmlFor: htmlForId,
  width = 'full',
  align = 'center',
  reserveMessageSpace = false,
  labelClassName = '',
  labelSuffix,
}: Readonly<FormFieldProps>) {
  const autoId = useId();
  const inputId = providedId ?? htmlForId ?? autoId;

  const validationState: ValidationState = error ? 'error' : 'neutral';
  const hasMessage = !!(error ?? helperText);

  const enhancedChild = isValidElement(children)
    ? cloneElement(
        children as ReactElement<{
          validationState?: ValidationState;
          disabled?: boolean;
          id?: string;
          'aria-describedby'?: string;
        }>,
        {
          validationState,
          ...(disabled ? { disabled: true } : {}),
          id: inputId,
          ...(hasMessage ? { 'aria-describedby': `${inputId}-message` } : {}),
        }
      )
    : children;

  const widthClass = inputWidthSizes[width];
  const alignmentClass = inputAlignClasses[align];

  // Only reserve space when there's no helper text but we want to prevent layout shift for errors
  const shouldReserveSpace = reserveMessageSpace && !helperText;

  return (
    <div
      data-slot="form-field"
      className={`flex flex-col ${widthClass} ${alignmentClass} ${formSpacing[spacing]}`}
    >
      {label && (
        <div className="flex items-center gap-1.5">
          <Label htmlFor={inputId} className={labelClassName}>
            {label}
            {required && (
              <span className="text-error ml-0.5" aria-hidden="true">
                *
              </span>
            )}
          </Label>
          {labelSuffix}
        </div>
      )}

      {enhancedChild}

      {(helperText ?? error ?? shouldReserveSpace ?? helperAction) && (
        <div className="flex items-baseline justify-between gap-3">
          <Text
            size="sm"
            id={`${inputId}-message`}
            aria-live="polite"
            variant={error ? 'error' : 'muted'}
            className="min-w-0"
          >
            {error ?? helperText ?? '\u00A0'}
          </Text>
          {helperAction && <div className="shrink-0">{helperAction}</div>}
        </div>
      )}
    </div>
  );
}

FormField.displayName = 'FormField';

export default FormField;
